import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  getAnalysisRun,
  getAnalysisV3,
  parseAnalysisV3Response,
  type AnalysisV3Result,
} from '@/api/avpulse-api'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import { getScoreVerdict } from '@/lib/emotionUtils'

interface ImageAnalysisData {
  runId: string
  runName?: string
  predictionUrl?: string
  originalUrl?: string
  uploadTimestamp?: number
  valence: number
  arousal: number
  score: number | null
  analysisV3: AnalysisV3Result | null
  error?: string
}

const scoreColor = (score: number) => {
  if (score >= 75) return '#22c55e'
  if (score >= 55) return '#f59e0b'
  return '#ef4444'
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

const formatDate = (timestamp?: number) => {
  if (!timestamp) return '—'
  const date =
    timestamp > 10_000_000_000
      ? new Date(timestamp)
      : new Date(timestamp * 1000)
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const toCompareMetrics = (
  score: number,
  analysisV3: AnalysisV3Result | null,
  arousal: number,
) => {
  const scrollStopping =
    analysisV3?.signals?.scroll_stopping_power ??
    analysisV3?.signals?.attention_strength ??
    clamp(Math.round(arousal / 20), 1, 5)
  const brandRecall =
    analysisV3?.signals?.brand_recall ??
    analysisV3?.signals?.brand_visibility ??
    clamp(Math.round((score / 100) * 5), 1, 5)

  return [
    { label: 'Scroll-stopping', value: scrollStopping, max: 5, pct: false },
    { label: 'Brand recall', value: brandRecall, max: 5, pct: false },
    { label: 'CTR potential', value: score, max: 100, pct: true },
  ]
}

const ScoreRing = ({ score }: { score: number }) => {
  const size = 36
  const stroke = 4
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const progress = clamp(score, 0, 100)
  const offset = c - (progress / 100) * c
  const color = scoreColor(score)

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke="var(--border)"
        strokeWidth={stroke}
        fill="none"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        fill="none"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  )
}

const CompareCreativeCard = ({ analysis }: { analysis: ImageAnalysisData }) => {
  if (analysis.error) {
    return (
      <article className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="border-b border-border bg-gradient-to-r from-primary/10 to-transparent px-4 py-3">
          <div className="text-sm font-semibold">{analysis.runName}</div>
          <div className="mt-1 font-mono text-[11px] text-muted-foreground">
            {analysis.runId.slice(-10)}
          </div>
        </div>
        <div className="p-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{analysis.error}</AlertDescription>
          </Alert>
        </div>
      </article>
    )
  }

  const safeScore = analysis.score ?? clamp(Math.round((analysis.valence + analysis.arousal) / 2), 0, 100)
  const verdict = getScoreVerdict(safeScore)
  const color = scoreColor(safeScore)
  const metrics = toCompareMetrics(safeScore, analysis.analysisV3, analysis.arousal)
  const meta = `${formatDate(analysis.uploadTimestamp)} · ${analysis.runId.slice(-8)}`

  return (
    <article className="flex min-h-[470px] flex-col overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border bg-gradient-to-r from-primary/10 to-transparent px-4 py-3">
        <div className="min-w-0">
          <div className="truncate text-[13px] font-semibold">
            {analysis.runName || `Analysis ${analysis.runId.slice(-8)}`}
          </div>
          <div className="mt-1 truncate text-[11px] text-muted-foreground">{meta}</div>
        </div>
        <div className="ml-3 flex items-center gap-2">
          <ScoreRing score={safeScore} />
          <div className="text-right">
            <div
              className="font-mono text-[15px] font-semibold tabular-nums"
              style={{ color }}
            >
              {safeScore}
            </div>
            <div className="text-[10px] text-muted-foreground">{verdict}</div>
          </div>
        </div>
      </div>

      <div className="h-[130px] overflow-hidden border-b border-border bg-muted/30">
        {analysis.predictionUrl ? (
          <img
            src={analysis.predictionUrl}
            alt={analysis.runName || analysis.runId}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
            Preview unavailable
          </div>
        )}
      </div>

      <div className="space-y-2.5 px-4 py-3">
        {metrics.map(metric => {
          const percent = metric.pct
            ? clamp(metric.value, 0, 100)
            : clamp((metric.value / metric.max) * 100, 0, 100)
          return (
            <div key={metric.label} className="flex items-center gap-2">
              <span className="flex-1 text-[11px] text-muted-foreground">
                {metric.label}
              </span>
              <div className="h-1 w-[84px] overflow-hidden rounded bg-muted">
                <div
                  className="h-full rounded"
                  style={{ width: `${percent}%`, backgroundColor: color }}
                />
              </div>
              <span
                className="w-8 text-right font-mono text-[10px] tabular-nums"
                style={{ color }}
              >
                {metric.pct ? Math.round(metric.value) : `${Math.round(metric.value)}/${metric.max}`}
              </span>
            </div>
          )
        })}
      </div>
    </article>
  )
}

const CompareCardSkeleton = () => (
  <article className="overflow-hidden rounded-xl border border-border bg-card">
    <div className="border-b border-border bg-gradient-to-r from-primary/10 to-transparent px-4 py-3">
      <div className="h-3.5 w-36 animate-pulse rounded bg-muted" />
      <div className="mt-2 h-2.5 w-28 animate-pulse rounded bg-muted" />
    </div>
    <div className="h-[130px] animate-pulse bg-muted/40" />
    <div className="space-y-2.5 px-4 py-3">
      {[0, 1, 2].map(row => (
        <div key={row} className="flex items-center gap-2">
          <div className="h-2.5 w-20 animate-pulse rounded bg-muted" />
          <div className="h-1 w-[84px] animate-pulse rounded bg-muted" />
          <div className="h-2.5 w-8 animate-pulse rounded bg-muted" />
        </div>
      ))}
    </div>
  </article>
)

const CompareImageAnalyses = () => {
  const [searchParams] = useSearchParams()
  const runIds = useMemo(() => {
    const runIdsParam = searchParams.get('runIds')
    return runIdsParam ? runIdsParam.split(',').filter(Boolean) : []
  }, [searchParams])

  const [analyses, setAnalyses] = useState<ImageAnalysisData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchAnalyses = async () => {
      if (runIds.length === 0) {
        setError('No run IDs provided for comparison')
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const analysesData = await Promise.all(
          runIds.map(async runId => {
            try {
              const runResult = await getAnalysisRun(runId)
              const prediction = runResult?.predictions?.predictions
              let analysisV3 = null
              if (prediction?.explanation_v3) {
                analysisV3 = parseAnalysisV3Response({
                  response: prediction.explanation_v3,
                  conversation_id: '',
                  response_type: '',
                  conversation_history: [],
                  current_step: '',
                })
              } else {
                try {
                  const v3Response = await getAnalysisV3(runId)
                  analysisV3 = parseAnalysisV3Response(v3Response)
                } catch {
                  analysisV3 = null
                }
              }

              return {
                runId,
                runName: runResult?.run?.runName,
                predictionUrl: prediction?.prediction_url,
                originalUrl: prediction?.original_url,
                uploadTimestamp: runResult?.run?.uploadTimestamp,
                valence: prediction?.emotion_valence ?? 0,
                arousal: prediction?.emotion_arousal ?? 0,
                score: typeof analysisV3?.score === 'number' ? analysisV3.score : null,
                analysisV3,
              } satisfies ImageAnalysisData
            } catch (err: unknown) {
              return {
                runId,
                runName: `Analysis ${runId.slice(-8)}`,
                predictionUrl: '',
                valence: 0,
                arousal: 0,
                score: null,
                analysisV3: null,
                error: err instanceof Error ? err.message : 'Failed to load analysis',
              } satisfies ImageAnalysisData
            }
          }),
        )
        setAnalyses(analysesData)
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load analyses')
        toast.error('Failed to load analyses')
      } finally {
        setLoading(false)
      }
    }

    fetchAnalyses()
  }, [runIds])

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-7xl px-6 py-8">
        <div className="mb-6">
          <p className="gradient-text text-4xl">Comparing creatives</p>
          <p className="text-sm text-muted-foreground">
            Side-by-side performance breakdown
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {Array.from({ length: Math.max(runIds.length || 2, 2) }).map((_, idx) => (
            <CompareCardSkeleton key={idx} />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-7xl px-6 py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    )
  }

  if (analyses.length === 0) {
    return (
      <div className="mx-auto w-full max-w-7xl px-6 py-8">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No Analyses</AlertTitle>
          <AlertDescription>No analyses found for comparison.</AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-6 py-8">
      <div className="mb-6">
        <p className="gradient-text text-4xl">Comparing {analyses.length} creatives</p>
        <p className="text-sm text-muted-foreground">
          Side-by-side performance breakdown
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {analyses.map(analysis => (
          <CompareCreativeCard key={analysis.runId} analysis={analysis} />
        ))}
      </div>
    </div>
  )
}

export default CompareImageAnalyses
